import {
  useCallback,
  useEffect,
  useMemo,
  FC,
  memo,
  useState,
  startTransition,
} from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface GroupTabProps {
  groupId: string;
  groupName: string;
  tags: { Name: string; InvestorTagId: number }[];
  initialExcludedTagNames?: string[];
  setFieldValue: (field: string, value: Record<string, string[]>) => void;
  getExcludedGroups: () => Record<string, string[]>;
}

const GroupTab: FC<GroupTabProps> = memo(
  ({
    groupId,
    groupName,
    tags,
    initialExcludedTagNames = [],
    setFieldValue,
    getExcludedGroups,
  }) => {
    const { t } = useTranslation();
    const [excludedTagNames, setExcludedTagNames] = useState<string[]>(
      initialExcludedTagNames,
    );

    useEffect(() => {
      setExcludedTagNames(initialExcludedTagNames);
    }, [initialExcludedTagNames]);

    const handleTagChange = useCallback(
      (tagName: string) => {
        setExcludedTagNames((prev) => {
          const isExcluded = prev.includes(tagName);
          const next = isExcluded
            ? prev.filter((n) => n !== tagName)
            : [...prev, tagName];
          startTransition(() => {
            const current = getExcludedGroups();
            setFieldValue('excludedGroups', {
              ...current,
              [groupId]: next,
            });
          });
          return next;
        });
      },
      [groupId, getExcludedGroups, setFieldValue],
    );

    // API can return null Name despite the string type — cast to acknowledge runtime reality
    type RuntimeTag = { Name: string | null; InvestorTagId: number };
    const sortedTags = useMemo(
      () =>
        (tags as unknown as RuntimeTag[])
          .filter(
            (tag): tag is { Name: string; InvestorTagId: number } =>
              tag.Name != null,
          )
          .sort((a, b) => a.Name.localeCompare(b.Name)),
      [tags],
    );

    if (sortedTags.length === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Typography color='text.secondary'>
            {t('filters.noSegmentsInGroup')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto', flex: 1 }}>
          <Typography
            variant='subtitle2'
            color='text.secondary'
            sx={{ mb: 2 }}
          >
            {t('filters.excludeGroupSegmentsDescription', { groupName })}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {sortedTags.map((tag) => (
              <FormControlLabel
                key={tag.InvestorTagId}
                control={
                  <Checkbox
                    checked={excludedTagNames.includes(tag.Name)}
                    onChange={() => {
                      handleTagChange(tag.Name);
                    }}
                    size='small'
                  />
                }
                label={tag.Name}
              />
            ))}
          </Box>
        </Paper>
      </Box>
    );
  },
);

GroupTab.displayName = 'GroupTab';

export default GroupTab;
